import { easeBufferBarStretchReveal } from "./audio-waveform-bar-geometry";

function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, value));
}

/** Per-bar dot → height bounce duration once the wipe wavefront reaches the bar. */
export const DEFAULT_BAR_STRETCH_BOUNCE_MS = 420;

/** 0–1 stretch progress for a single bar's timed bounce (independent of wipe speed). */
export function resolvePerBarStretchProgress(
	elapsedMs: number,
	durationMs = DEFAULT_BAR_STRETCH_BOUNCE_MS,
	applyBounce = true,
): number {
	const linear = durationMs <= 0 ? 1 : clamp(elapsedMs / durationMs, 0, 1);
	return applyBounce ? easeBufferBarStretchReveal(linear) : linear;
}

export function resolvePerBarLinearStretchProgress(
	elapsedMs: number,
	durationMs = DEFAULT_BAR_STRETCH_BOUNCE_MS,
): number {
	return durationMs <= 0 ? 1 : clamp(elapsedMs / durationMs, 0, 1);
}

/** 0–100 linear time sweep capped by actual buffer — never exceeds actual. */
export function resolveTimeBasedRevealPercent(
	elapsedMs: number,
	actualPercent: number,
	totalDurationMs: number,
): number {
	const clampedActual = clamp(actualPercent, 0, 100);
	if (totalDurationMs <= 0) {
		return clampedActual;
	}
	const timePercent = clamp((elapsedMs / totalDurationMs) * 100, 0, 100);
	return Math.min(timePercent, clampedActual);
}

export function resolveBarStartPercent(
	index: number,
	barCount: number,
): number {
	return (index / barCount) * 100;
}

/** Linear time (ms) into the wipe when the wavefront reaches this bar. */
export function resolveBarStretchWavefrontMs(
	index: number,
	barCount: number,
	wipeDurationMs: number,
): number {
	if (wipeDurationMs <= 0 || barCount <= 0) {
		return 0;
	}
	return (resolveBarStartPercent(index, barCount) / 100) * wipeDurationMs;
}

/** Default left-to-right buffer wipe duration (matches voice-sample prepare gate). */
export const DEFAULT_BUFFER_REVEAL_WIPE_MS = 1000;

/** When the wipe wavefront reaches the last bar (ms into the linear sweep). */
export function resolveLastBarActivationMs(
	wipeDurationMs: number,
	barCount: number,
): number {
	if (barCount <= 1 || wipeDurationMs <= 0) {
		return 0;
	}
	const lastBarStartPercent = resolveBarStartPercent(barCount - 1, barCount);
	return (lastBarStartPercent / 100) * wipeDurationMs;
}

/** Min prepare time so every bar can finish its post-activation stretch bounce. */
export function resolveBufferRevealPrepareDurationMs(
	wipeDurationMs: number,
	barCount: number,
	barStretchMs = DEFAULT_BAR_STRETCH_BOUNCE_MS,
): number {
	const lastBarFinishMs =
		resolveLastBarActivationMs(wipeDurationMs, barCount) + barStretchMs;
	return Math.ceil(Math.max(wipeDurationMs, lastBarFinishMs));
}

/** 0–1 fade-in for a bar as buffer progress reaches it (smooth per-bar reveal). */
export function resolveBarBufferRevealOpacity(
	index: number,
	barCount: number,
	bufferPercent: number,
): number {
	const barWidth = 100 / barCount;
	const barStart = resolveBarStartPercent(index, barCount);
	return clamp((bufferPercent - barStart) / barWidth, 0, 1);
}

/** 0–1 stretch + fade progress for a bar as buffer reveal sweeps left-to-right. */
export function resolveBarBufferRevealProgress(
	index: number,
	barCount: number,
	bufferPercent: number,
): number {
	return resolveBarBufferRevealOpacity(index, barCount, bufferPercent);
}

/** 0–1 played fraction within a single bar for continuous playhead highlighting. */
export function resolveBarPlayedFraction(
	index: number,
	barCount: number,
	progressPercent: number,
): number {
	const barWidth = 100 / barCount;
	const barStart = (index / barCount) * 100;
	return clamp((progressPercent - barStart) / barWidth, 0, 1);
}

/** 0–100 buffered extent from the media element (actual ranges only). */
export function resolveBufferedEndPercent(
	audio: HTMLMediaElement | null | undefined,
): number {
	if (!audio) {
		return 0;
	}

	const { duration, buffered } = audio;
	if (!Number.isFinite(duration) || duration <= 0) {
		return 0;
	}

	if (buffered.length === 0) {
		return 0;
	}

	let maxBufferedEnd = 0;
	for (let index = 0; index < buffered.length; index += 1) {
		maxBufferedEnd = Math.max(maxBufferedEnd, buffered.end(index));
	}

	return clamp((maxBufferedEnd / duration) * 100, 0, 100);
}

/** Min seconds of audio that must be buffered before playback (or full track if shorter). */
export const DEFAULT_PLAYBACK_BUFFER_THRESHOLD_SECONDS = 10;

/** Required buffer % before playback: min(thresholdSeconds, duration) / duration × 100. */
export function resolvePlaybackBufferThresholdPercent(
	durationSeconds: number,
	thresholdSeconds = DEFAULT_PLAYBACK_BUFFER_THRESHOLD_SECONDS,
): number {
	if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
		return 100;
	}
	const requiredSeconds = Math.min(thresholdSeconds, durationSeconds);
	return clamp((requiredSeconds / durationSeconds) * 100, 0, 100);
}

/** Combines buffer reveal with played/unplayed highlight into final bar opacity. */
export function resolveBarVisualOpacity(
	index: number,
	barCount: number,
	progressPercent: number,
	bufferPercent: number,
): number {
	const reveal = resolveBarBufferRevealOpacity(
		index,
		barCount,
		bufferPercent,
	);
	const playedFraction = resolveBarPlayedFraction(
		index,
		barCount,
		progressPercent,
	);
	const highlight = 0.2 + 0.8 * playedFraction;
	return reveal * highlight;
}
